import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  ExternalLink,
  Loader2,
  Zap,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import {
  BattleNetIcon,
  EpicGamesIcon,
  RiotIcon,
  SteamIcon,
  XboxIcon,
} from "../../../components/icons/BrandIcons";
import { Button, Input } from "../../../components/ui";
import { trpc } from "../../../lib/trpc";

export const Route = createFileRoute("/app/onboarding/step-3")({
  component: LinkAccountsStep,
});

type PlatformId = "riot" | "steam" | "microsoft" | "epic" | "battlenet";

type PlatformConfig = {
  id: PlatformId;
  name: string;
  icon: (props: { className?: string }) => React.ReactNode;
  color: string;
  usernameLabel: string;
  usernamePlaceholder: string;
  hasDiscriminator: boolean;
  discriminatorPlaceholder?: string;
  idLabel: string;
  idPlaceholder: string;
  oauthReady: boolean;
};

const platforms: PlatformConfig[] = [
  {
    id: "steam",
    name: "Steam",
    icon: SteamIcon,
    color: "text-surface-200",
    usernameLabel: "Steam Username",
    usernamePlaceholder: "Your Steam display name",
    hasDiscriminator: false,
    idLabel: "Steam ID (optional)",
    idPlaceholder: "e.g. 76561198...",
    oauthReady: false,
  },
  {
    id: "epic",
    name: "Epic Games",
    icon: EpicGamesIcon,
    color: "text-surface-200",
    usernameLabel: "Epic Display Name",
    usernamePlaceholder: "Your Epic username",
    hasDiscriminator: false,
    idLabel: "Epic Account ID (optional)",
    idPlaceholder: "Leave blank if you don't know it",
    oauthReady: false,
  },
  {
    id: "microsoft",
    name: "Xbox / Microsoft",
    icon: XboxIcon,
    color: "text-green-400",
    usernameLabel: "Gamertag",
    usernamePlaceholder: "Your Xbox Gamertag",
    hasDiscriminator: false,
    idLabel: "XUID (optional)",
    idPlaceholder: "Leave blank if you don't know it",
    oauthReady: false,
  },
  {
    id: "riot",
    name: "Riot Games",
    icon: RiotIcon,
    color: "text-error-400",
    usernameLabel: "Riot ID",
    usernamePlaceholder: "Player",
    hasDiscriminator: true,
    discriminatorPlaceholder: "NA1",
    idLabel: "PUUID (optional)",
    idPlaceholder: "Leave blank if you don't know it",
    oauthReady: false,
  },
  {
    id: "battlenet",
    name: "Battle.net",
    icon: BattleNetIcon,
    color: "text-sky-400",
    usernameLabel: "BattleTag",
    usernamePlaceholder: "Player",
    hasDiscriminator: true,
    discriminatorPlaceholder: "1234",
    idLabel: "Battle.net ID (optional)",
    idPlaceholder: "Leave blank if you don't know it",
    oauthReady: false,
  },
];

function LinkAccountsStep() {
  const navigate = useNavigate();
  const { data: linkedAccounts, refetch } = trpc.linkedAccount.list.useQuery();
  const [expandedPlatform, setExpandedPlatform] = useState<PlatformId | null>(null);

  const linkedPlatforms = new Set(linkedAccounts?.map((a) => a.platform) ?? []);

  return (
    <div className="animate-fade-up p-4 md:p-6">
      <div className="relative rounded-2xl bg-surface-900/80 border border-surface-700/50 overflow-hidden">
        {/* Top accent */}
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-500/40 to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(38,217,181,0.04)_0%,_transparent_60%)] pointer-events-none" />

        <div className="relative px-6 sm:px-8 py-8 space-y-8">
          {/* Header */}
          <div className="text-center space-y-2">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Link Your Accounts</h1>
            <p className="text-sm text-surface-400">Connect your gaming platforms to get started</p>
          </div>

          {/* Platform cards */}
          <div className="space-y-3">
            {platforms.map((platform) => {
              const isLinked = linkedPlatforms.has(platform.id);
              const isExpanded = expandedPlatform === platform.id && !isLinked;
              const linkedAccount = linkedAccounts?.find((a) => a.platform === platform.id);

              return (
                <PlatformCard
                  key={platform.id}
                  platform={platform}
                  isLinked={isLinked}
                  isExpanded={isExpanded}
                  linkedAccount={linkedAccount}
                  onToggle={() => {
                    if (!isLinked) {
                      setExpandedPlatform(isExpanded ? null : platform.id);
                    }
                  }}
                  onLinkSuccess={() => {
                    setExpandedPlatform(null);
                    void refetch();
                  }}
                />
              );
            })}
          </div>

          <p className="text-xs text-surface-500 text-center">
            You can always link or update accounts later in Settings
          </p>

          {/* Actions */}
          <div className="flex items-center justify-between pt-2">
            <Button
              variant="ghost"
              onClick={() => void navigate({ to: "/app/onboarding/step-2" })}
              leftIcon={<ArrowLeft className="size-4" />}
            >
              Back
            </Button>
            <Button
              variant="primary"
              onClick={() => void navigate({ to: "/app/onboarding/step-4" })}
              rightIcon={<ArrowRight className="size-4" />}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Platform Card
// ---------------------------------------------------------------------------

function PlatformCard({
  platform,
  isLinked,
  isExpanded,
  linkedAccount,
  onToggle,
  onLinkSuccess,
}: {
  platform: PlatformConfig;
  isLinked: boolean;
  isExpanded: boolean;
  linkedAccount?: { platformUsername: string; platformUsernameDiscriminator?: string | null };
  onToggle: () => void;
  onLinkSuccess: () => void;
}) {
  const [showManualForm, setShowManualForm] = useState(false);
  const Icon = platform.icon;

  const handleOAuthClick = () => {
    toast("OAuth linking coming soon!", {
      description: `${platform.name} sign-in will be available in a future update.`,
      icon: <Zap className="size-4 text-brand-400" />,
    });
  };

  return (
    <div
      className={`
        rounded-xl border transition-all duration-200 overflow-hidden
        ${
          isLinked
            ? "border-brand-500/30 bg-brand-500/5"
            : isExpanded
              ? "border-surface-600 bg-surface-800/60"
              : "border-surface-700/40 bg-surface-800/30 hover:border-surface-600 hover:bg-surface-800/60"
        }
      `}
    >
      {/* Platform header row */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-4 px-5 py-4 text-left"
      >
        <div
          className={`flex items-center justify-center size-10 rounded-xl transition-colors ${
            isLinked ? "bg-brand-500/15 text-brand-400" : `bg-surface-700/50 ${platform.color}`
          }`}
        >
          <Icon className="size-6" />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`font-semibold text-sm ${isLinked ? "text-white" : "text-surface-200"}`}>
            {platform.name}
          </p>
          {isLinked && linkedAccount && (
            <p className="text-xs text-brand-400/80 mt-0.5">
              {linkedAccount.platformUsername}
              {linkedAccount.platformUsernameDiscriminator && (
                <span className="text-brand-400/50">
                  #{linkedAccount.platformUsernameDiscriminator}
                </span>
              )}
            </p>
          )}
        </div>
        {isLinked ? (
          <div className="flex items-center justify-center size-6 rounded-full bg-brand-500 text-surface-900">
            <Check className="size-3.5" />
          </div>
        ) : (
          <ChevronDown
            className={`size-4 text-surface-500 transition-transform duration-200 ${
              isExpanded ? "rotate-180" : ""
            }`}
          />
        )}
      </button>

      {/* Expanded content: OAuth + manual toggle */}
      {isExpanded && (
        <div className="px-5 pb-5 pt-1 border-t border-surface-700/30 animate-fade-in">
          {!showManualForm ? (
            <div className="space-y-4 pt-3">
              <Button
                variant="primary"
                className="w-full"
                leftIcon={<ExternalLink className="size-4" />}
                onClick={handleOAuthClick}
              >
                Link with {platform.name}
              </Button>

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-surface-700/50" />
                <span className="text-[11px] uppercase tracking-wider text-surface-600 font-medium">
                  or
                </span>
                <div className="flex-1 h-px bg-surface-700/50" />
              </div>

              <Button
                variant="ghost"
                className="w-full"
                size="sm"
                onClick={() => setShowManualForm(true)}
              >
                Enter details manually
              </Button>
            </div>
          ) : (
            <div className="pt-3">
              <button
                type="button"
                onClick={() => setShowManualForm(false)}
                className="text-xs font-medium text-surface-500 hover:text-surface-300 transition-colors mb-3"
              >
                &larr; Back
              </button>
              <LinkForm platform={platform} onSuccess={onLinkSuccess} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Link Form
// ---------------------------------------------------------------------------

function LinkForm({ platform, onSuccess }: { platform: PlatformConfig; onSuccess: () => void }) {
  const [username, setUsername] = useState("");
  const [discriminator, setDiscriminator] = useState("");
  const [userId, setUserId] = useState("");

  const linkMutation = trpc.linkedAccount.linkManual.useMutation({
    onSuccess: () => {
      toast.success(`${platform.name} account linked!`);
      onSuccess();
    },
    onError: (err) => {
      if (err.data?.code === "CONFLICT") {
        toast.error(`You already have a ${platform.name} account linked`);
      } else {
        toast.error(`Failed to link ${platform.name} account`);
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim()) return;

    linkMutation.mutate({
      platform: platform.id,
      platformUserId: userId.trim() || username.trim(),
      platformUsername: username.trim(),
      platformUsernameDiscriminator: discriminator.trim() || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {platform.hasDiscriminator ? (
        <div className="flex gap-3">
          <div className="flex-1">
            <Input
              label={platform.usernameLabel}
              placeholder={platform.usernamePlaceholder}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
            />
          </div>
          <div className="w-28">
            <Input
              label="Tag"
              placeholder={platform.discriminatorPlaceholder}
              value={discriminator}
              onChange={(e) => setDiscriminator(e.target.value)}
            />
          </div>
        </div>
      ) : (
        <Input
          label={platform.usernameLabel}
          placeholder={platform.usernamePlaceholder}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoFocus
        />
      )}

      <Input
        label={platform.idLabel}
        placeholder={platform.idPlaceholder}
        value={userId}
        onChange={(e) => setUserId(e.target.value)}
      />

      <div className="flex justify-end pt-1">
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={!username.trim() || linkMutation.isPending}
          leftIcon={
            linkMutation.isPending ? <Loader2 className="size-3.5 animate-spin" /> : undefined
          }
        >
          {linkMutation.isPending ? "Linking..." : "Link Account"}
        </Button>
      </div>
    </form>
  );
}
